import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Server, Download, Search, ChevronLeft, ChevronRight, 
  AlertTriangle, CheckCircle, Eye, Filter, X,
  ArrowUpDown, ArrowUp, ArrowDown
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateOnlyARG } from '../../lib/utils';

// Sort icon component
const SortIcon = ({ field, sortConfig }) => {
  if (sortConfig.field !== field) {
    return <ArrowUpDown className="h-4 w-4 text-muted-foreground/50" />;
  }
  return sortConfig.dir === 'asc' 
    ? <ArrowUp className="h-4 w-4 text-primary" />
    : <ArrowDown className="h-4 w-4 text-primary" />;
};

const PendingServersReport = () => {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({
    total_servers: 0,
    pending: 0,
    relevados: 0,
    by_tipo: [],
    by_site: []
  });
  
  // Data
  const [servers, setServers] = useState({ items: [], total: 0, page: 1, pages: 1 });
  
  // Filters
  const [filters, setFilters] = useState({ search: '', tipo: '', site: '' });
  const [filterOptions, setFilterOptions] = useState({ tipos: [], sites: [] });
  
  // Sort
  const [sortConfig, setSortConfig] = useState({ field: 'hostname', dir: 'asc' });

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/reports/pending-servers/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  // Fetch filter options
  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await api.get('/reports/pending-servers/filter/options');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  }, []);

  // Fetch servers
  const fetchServers = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ 
        page: page.toString(), 
        page_size: '50',
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir
      });
      if (filters.search) params.append('search', filters.search);
      if (filters.tipo) params.append('tipo', filters.tipo);
      if (filters.site) params.append('site', filters.site);
      
      const response = await api.get(`/reports/pending-servers?${params}`);
      setServers(response.data);
    } catch (error) {
      console.error('Error fetching servers:', error);
      toast.error('Error al cargar servidores');
    } finally {
      setLoading(false);
    }
  }, [filters, sortConfig]);

  // Initial load
  useEffect(() => {
    fetchStats();
    fetchFilterOptions();
  }, [fetchStats, fetchFilterOptions]);

  // Debounced fetch on filter/sort change
  useEffect(() => {
    const t = setTimeout(() => {
      fetchServers(1);
    }, 800);
    return () => clearTimeout(t);
  }, [filters, sortConfig, fetchServers]);

  // Handle sort
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle export
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.tipo) params.append('tipo', filters.tipo);
      if (filters.site) params.append('site', filters.site);
      
      const response = await api.get(`/reports/pending-servers/export?${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `servidores_sin_relevar_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Exportación completada');
    } catch (error) {
      toast.error('Error al exportar');
    } finally {
      setExporting(false);
    }
  };

  // Format date
  const formatDate = (dateStr) => formatDateOnlyARG(dateStr);

  // Percentage
  const pendingPercent = stats.total_servers > 0 
    ? Math.round((stats.pending / stats.total_servers) * 100) 
    : 0;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Servidores Sin Relevar</h1>
            <p className="page-subtitle">Servidores que no tienen detalle de hardware registrado</p>
          </div>
          <Button onClick={handleExport} disabled={exporting} variant="outline" data-testid="export-btn">
            <Download className="h-4 w-4 mr-2" />
            {exporting ? 'Exportando...' : 'Exportar'}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="pending-stats">
          <div className="stats-card flex items-center gap-3">
            <Server className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value">{stats.total_servers}</div>
              <div className="stats-card-label">Total Servidores</div>
            </div>
          </div>
          <div className="stats-card flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-yellow-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-yellow-500">{stats.pending}</div>
              <div className="stats-card-label">Sin Relevar ({pendingPercent}%)</div>
            </div>
          </div>
          <div className="stats-card flex items-center gap-3">
            <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-green-500">{stats.relevados}</div>
              <div className="stats-card-label">Relevados</div>
            </div>
          </div>
        </div>

        {/* Distribution Cards */}
        {(stats.by_tipo.length > 0 || stats.by_site.length > 0) && (
          <div className="grid grid-cols-2 gap-4">
            {/* By Tipo */}
            {stats.by_tipo.length > 0 && (
              <Card className="card-subtle">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold">Por Tipo de Dispositivo</h3>
                    {filters.tipo && (
                      <button 
                        onClick={() => setFilters(prev => ({ ...prev, tipo: '' }))}
                        className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
                      >
                        <X className="h-3 w-3" /> Limpiar
                      </button>
                    )}
                  </div>
                  <div className="space-y-1">
                    {stats.by_tipo.slice(0, 5).map((item, idx) => (
                      <div 
                        key={idx} 
                        onClick={() => item.id && setFilters(prev => ({ ...prev, tipo: String(item.id) }))}
                        className={`flex items-center justify-between text-sm px-2 py-1.5 rounded cursor-pointer transition-colors ${
                          filters.tipo === String(item.id) 
                            ? 'bg-primary/20 text-primary' 
                            : 'hover:bg-muted/50'
                        }`}
                      >
                        <span className={filters.tipo === String(item.id) ? 'text-primary font-medium' : 'text-muted-foreground'}>
                          {item.tipo}
                        </span>
                        <span className={`font-medium ${filters.tipo === String(item.id) ? 'text-primary' : 'text-yellow-500'}`}>
                          {item.count}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* By Site */}
            {stats.by_site.length > 0 && (
              <Card className="card-subtle">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold">Por Site</h3>
                    {filters.site && (
                      <button 
                        onClick={() => setFilters(prev => ({ ...prev, site: '' }))}
                        className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
                      >
                        <X className="h-3 w-3" /> Limpiar
                      </button>
                    )}
                  </div>
                  <div className="space-y-1">
                    {stats.by_site.slice(0, 5).map((item, idx) => (
                      <div 
                        key={idx} 
                        onClick={() => item.id && setFilters(prev => ({ ...prev, site: String(item.id) }))}
                        className={`flex items-center justify-between text-sm px-2 py-1.5 rounded cursor-pointer transition-colors ${
                          filters.site === String(item.id) 
                            ? 'bg-primary/20 text-primary' 
                            : 'hover:bg-muted/50'
                        }`}
                      >
                        <span className={filters.site === String(item.id) ? 'text-primary font-medium' : 'text-muted-foreground'}>
                          {item.site}
                        </span>
                        <span className={`font-medium ${filters.site === String(item.id) ? 'text-primary' : 'text-yellow-500'}`}>
                          {item.count}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Filters */}
        <Card className="card-subtle">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filtros:</span>
              </div>
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Buscar hostname, IP..." 
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="h-9 w-48"
                  data-testid="filter-search"
                />
              </div>
              <Select 
                value={filters.tipo || "all"} 
                onValueChange={(v) => setFilters(prev => ({ ...prev, tipo: v === "all" ? "" : v }))}
              >
                <SelectTrigger className="h-9 w-40">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos</SelectItem>
                  {filterOptions.tipos.map(t => (
                    <SelectItem key={t.id} value={String(t.id)}>{t.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select 
                value={filters.site || "all"} 
                onValueChange={(v) => setFilters(prev => ({ ...prev, site: v === "all" ? "" : v }))}
              >
                <SelectTrigger className="h-9 w-40">
                  <SelectValue placeholder="Site" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los sites</SelectItem>
                  {filterOptions.sites.map(s => (
                    <SelectItem key={s.id} value={String(s.id)}>{s.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(filters.search || filters.tipo || filters.site) && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setFilters({ search: '', tipo: '', site: '' })}
                >
                  <X className="h-4 w-4 mr-1" /> Limpiar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="card-subtle overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : servers.items.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500 opacity-50" />
                <p>No hay servidores sin relevar</p>
                <p className="text-sm mt-2">Todos los servidores tienen detalle de hardware</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table-subtle">
                  <thead>
                    <tr>
                      <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('hostname')}>
                        <div className="flex items-center gap-1">
                          HOSTNAME <SortIcon field="hostname" sortConfig={sortConfig} />
                        </div>
                      </th>
                      <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('ip')}>
                        <div className="flex items-center gap-1">
                          IP <SortIcon field="ip" sortConfig={sortConfig} />
                        </div>
                      </th>
                      <th>DESCRIPCIÓN</th>
                      <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('tipo')}>
                        <div className="flex items-center gap-1">
                          TIPO <SortIcon field="tipo" sortConfig={sortConfig} />
                        </div>
                      </th>
                      <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('site')}>
                        <div className="flex items-center gap-1">
                          SITE <SortIcon field="site" sortConfig={sortConfig} />
                        </div>
                      </th>
                      <th>ESTADO</th>
                      <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('last_update')}>
                        <div className="flex items-center gap-1">
                          ÚLTIMA ACT. <SortIcon field="last_update" sortConfig={sortConfig} />
                        </div>
                      </th>
                      <th className="w-16 text-right">ACCIÓN</th>
                    </tr>
                  </thead>
                  <tbody>
                    {servers.items.map((server, idx) => (
                      <tr key={`${server.hostname}-${idx}`}>
                        <td className="font-medium text-primary">{server.hostname}</td>
                        <td className="font-mono text-sm text-muted-foreground">{server.ip || '-'}</td>
                        <td className="text-muted-foreground max-w-[200px] truncate" title={server.description || ''}>
                          {server.description || '-'}
                        </td>
                        <td>
                          <span className="badge badge-warning">{server.tipo_nombre}</span>
                        </td>
                        <td className="text-muted-foreground">{server.site_nombre}</td>
                        <td>
                          <span className={`badge ${
                            server.estado_nombre?.toLowerCase().includes('running') ? 'badge-success' : 'badge-default'
                          }`}>
                            {server.estado_nombre}
                          </span>
                        </td>
                        <td className="text-muted-foreground text-sm">{formatDate(server.last_update)}</td>
                        <td className="text-right">
                          <Link to={`/assets/servers/${server.hostname}`}>
                            <button className="action-btn" title="Ver detalle" data-testid={`view-${server.hostname}`}>
                              <Eye className="h-3.5 w-3.5" />
                            </button>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {servers.total > 0 && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {servers.total} servidores sin relevar
            </div>
            {servers.pages > 1 && (
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => fetchServers(servers.page - 1)} 
                  disabled={servers.page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  {servers.page} / {servers.pages}
                </span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => fetchServers(servers.page + 1)} 
                  disabled={servers.page === servers.pages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PendingServersReport;
